import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Button from '@material-ui/core/Button'
import OpenInNewOutlinedIcon from '@material-ui/icons/OpenInNewOutlined'
import { ExternalLink } from '@lib/pga-coach/coach-profile-icons'
import ButtonCta from '@components/atoms/button-cta'
import Box from '@material-ui/core/Box'
import useIsMobile from '@hooks/useIsMobile'
import { gtmEvent } from '@lib/gtm-utils'
import * as amplitude from '@amplitude/analytics-browser'
import { useRouter } from 'next/router'
import isPgaLink from '@utils/isPgaLink'
import useExperimentVariant from '@lib/experiment/useExperimentVariant'
import useFeatureFlags from '@lib/feature-flags/useFeatureFlags'
import { flags } from '@lib/feature-flags/constants'
import { coachIsPgaScheduleBookable } from '@lib/pga-coach/coach-scheduling'
import useStyles from '../coach-profile-cta-banner/coach-profile-cta-banner.styles'

/* If 'Book Now' is enabled, always route to the coach's schedule page.
However, only display the words "book now" if they are bookable (active lessons). */
const CoachProfileCTAButtons = ({ coach, ctaPosition }) => {
  const {
    bookable, bookingUrl, firstName, lastName, displayName, coachProfileId, introductionVideoUrl,
    coach: { preferences, freeTierLimitsReached }, memberType,
  } = coach

  const coachName = (firstName && lastName) ? `${firstName} ${lastName}` : displayName
  const profileIsActive = coach.status === 'ACTIVE'
  const { bookNowEnabled, acceptsLeads } = preferences
  const isExternalLink = !isPgaLink(bookingUrl)
  const [bookingWithReturnUrl, setBookingWithReturnUrl] = useState(bookingUrl)
  const classes = useStyles({ memberType })
  const isMobile = useIsMobile()
  const router = useRouter()

  const isLeadsLimitReached = freeTierLimitsReached.includes('LEADS')
  const showContactForm = acceptsLeads && !isLeadsLimitReached
  const isBookable = coachIsPgaScheduleBookable(coach)
  const showBookingButton = (bookNowEnabled && isExternalLink) || (bookNowEnabled && isBookable)

  // Experiment variants and feature flags
  const bookNowVariant = showBookingButton ? useExperimentVariant('book-now-button-label-experiment') : null
  const buttonLabel = bookNowVariant?.payload?.['display-label'] || 'Book Now'
  const [contactButtonVariant, coachProfileUxEnabled] = useFeatureFlags([
    flags.FLAG_CONTACT_BUTTON_LABEL,
    flags.FLAG_COACH_PROFILE_UX,
  ])
  const contactButtonLabel = contactButtonVariant
    ? contactButtonVariant
      .toString()
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase())
    : 'Send a Request'

  const scrollToForm = () => {
    const formElement = document.getElementById('lead-form')
    const offset = isMobile ? 80 : 180
    if (formElement) {
      const formPosition = formElement.offsetTop
      window.scrollTo({ top: formPosition - offset, behavior: 'smooth' })
    }
  }

  useEffect(() => {
    setBookingWithReturnUrl(isExternalLink ? bookingUrl : `${bookingUrl}?returnUrl=${encodeURIComponent(window.location.href)}`)
    const shouldOpenForm = window.location.hash === '#send-request-form'
    if (shouldOpenForm) {
      scrollToForm()
    }
  }, [])

  useEffect(() => {
    if (!showContactForm) return
    if (typeof window === 'undefined') return
    amplitude.track('experiment-contact-button-label-impression', {
      contact_button_label: contactButtonLabel,
      source: 'coach profile',
      page_path: router?.asPath,
    })
  }, [showContactForm, contactButtonLabel, coachProfileId, router?.asPath])
  const buttonProps = {
    component: 'a',
    'data-gtm-profile-coach': coachName,
    'data-gtm-cta-position': ctaPosition,
  }
  const bookNowProps = {
    variant: !memberType ? 'outlined' : 'contained',
    color: 'unset',
    ...buttonProps,
    className: `${classes.button} ${classes.bookButton}`,
  }
  const textProps = {
    variant: 'text',
    color: 'secondary',
    ...buttonProps,
    className: classes.button,
  }

  const handleContactClick = () => {
    gtmEvent({
      event: 'click-contact-button',
      attributes: {
        coach_name: coachName,
        coach_profile_id: coachProfileId,
        contact_button_label: contactButtonLabel,
      },
    })
    scrollToForm()
  }

  const handleEventing = () => amplitude.track('click-book-now-button', {
    bookable,
    coach_profile_id: coachProfileId,
    coach_name: coachName,
    has_video: !!introductionVideoUrl,
    source: 'coach profile',
    page_path: router.asPath,
  })

  const ContactButton = showContactForm ? (
    <Button
      onClick={handleContactClick}
      data-gtm-button-name="consolidated-contact-bookable-coach"
      {...textProps}
      href="#send-request-form"
    >
      {contactButtonLabel}
    </Button>
  ) : null

  // If the coach has an external booking URL configured, link to that external site.
  if (bookNowEnabled && isExternalLink) {
    return (
      <Box className={classes.buttonContainer}>
        {ContactButton}
        <ButtonCta
          externalLink={bookingUrl}
          dataAttrs={{
            'data-gtm-button-name': 'book-now-ext',
          }}
          onClick={handleEventing}
          {...bookNowProps}
        >
          {buttonLabel}
          {coachProfileUxEnabled ? (
            <ExternalLink className={classes.externalLinkIcon} aria-hidden />
          ) : (
            <OpenInNewOutlinedIcon
              className={classes.externalLinkIcon}
              fontSize="small"
              aria-label="External Link"
            />
          )}
        </ButtonCta>
      </Box>
    )
  }
  // If the coach has lessons to book, link to the coach's schedule page.
  if (bookNowEnabled && isBookable) {
    return (
      <Box className={classes.buttonContainer}>
        {ContactButton}
        <ButtonCta
          href={bookingWithReturnUrl}
          dataAttrs={{
            'data-gtm-button-name': 'book-now',
          }}
          onClick={handleEventing}
          {...bookNowProps}
        >
          {buttonLabel}
        </ButtonCta>
      </Box>
    )
  }
  // If the Coach user has Book now enabled,
  // without lessons to book(either no lesson types or all lesson types are inactive)
  // FF is FALSE indicates that we should send the user to the request coaching intake form on MyPGA
  const coachWantsToBeRequested = bookNowEnabled && !isBookable && !isExternalLink
  if (coachWantsToBeRequested && profileIsActive) {
    return (
      <Box className={classes.buttonContainer}>
        {ContactButton}
      </Box>
    )
  }
  // Coach has no bookable or visible lessons
  // Book now is disabled
  // Intake form enabled (FF is TRUE)
  if (profileIsActive) {
    return (
      <Box className={classes.buttonContainer}>
        {ContactButton}
      </Box>
    )
  }

  return null
}

CoachProfileCTAButtons.propTypes = {
  coach: PropTypes.shape({
    bookable: PropTypes.bool.isRequired,
    bookingUrl: PropTypes.string,
    displayName: PropTypes.string,
    coachProfileId: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    introductionVideoUrl: PropTypes.string,
    coach: PropTypes.shape({
      lessonTypes: PropTypes.arrayOf(PropTypes.shape({
        status: PropTypes.string.isRequired,
      })),
      preferences: PropTypes.shape({
        bookNowEnabled: PropTypes.bool.isRequired,
        acceptsLeads: PropTypes.bool.isRequired,
      }),
      freeTierLimitsReached: PropTypes.array,
    }).isRequired,
    status: PropTypes.string.isRequired,
    memberType: PropTypes.string,
  }).isRequired,
  ctaPosition: PropTypes.string.isRequired,
}

export default CoachProfileCTAButtons
